/**
 * API Adapters
 * 
 * This module contains adapters that transform data between frontend and backend formats.
 * Using adapters helps decouple the frontend models from backend DTOs and handles
 * inconsistencies between the two systems.
 */

import { Project } from "../types/project";
import { ProjectFormData } from "../types/project-form";
import { TestCycle, AddTestCycleFormData } from "../types/test-cycle";

/**
 * Adapts a project from frontend format to backend format
 * Handles transformations like fishbone analysis structure
 */
export const adaptProjectForApi = (project: ProjectFormData): any => {
  // Create a deep copy to avoid mutating the original
  const adaptedProject = JSON.parse(JSON.stringify(project));
  
  // Set proper status - backend expects specific status values
  
  
  // Handle submission status - API expects 'ongoing' for new projects
  if (!adaptedProject.status) {
    adaptedProject.status = 'ongoing';
  } else if (adaptedProject.status === 'completed') {
    // API expects 'past_completion' for completed projects
    adaptedProject.status = 'past_completion';
  } else if (adaptedProject.status === 'draft') {
    // Preserve draft status - drafts should stay as drafts in the database
  } else if (!adaptedProject.id) {
    // Force ongoing status for any new non-draft project (no ID)
    adaptedProject.status = 'ongoing';
  }
  
  
  // Handle fishbone analysis transformation (nested to flat)
  // CRITICAL FIX: Check if data is already transformed by frontend (peopleCauses array exists)
  const alreadyTransformed = adaptedProject.peopleCauses || adaptedProject.processCauses || 
                             adaptedProject.policyCauses || adaptedProject.placeCauses || 
                             adaptedProject.provisionCauses;
  
  
  
  if (alreadyTransformed) {
   
    
    // CRITICAL: Delete fishBoneAnalysis to prevent backend from re-transforming and overwriting
    // The backend checks for fishBoneAnalysis existence and transforms it, which overwrites our data
    delete adaptedProject.fishBoneAnalysis;
    
    adaptedProject._fishboneDataTransformed = true;
  } else if (adaptedProject.fishBoneAnalysis && typeof adaptedProject.fishBoneAnalysis === 'object') {
    transformFishboneForBackend(adaptedProject);
    // Add a marker to indicate this data has already been transformed
    adaptedProject._fishboneDataTransformed = true;
  } 
  
  // CRITICAL FIX: Ensure Decision Matrix data is included
  // Ensure decisionMatrix is always a valid array
if (!adaptedProject.decisionMatrix || !Array.isArray(adaptedProject.decisionMatrix)) {
  adaptedProject.decisionMatrix = [];
}
  
  // Log CHS Team Members to verify all fields are included
  // Ensure chsTeamMembers is always a valid array
if (!adaptedProject.chsTeamMembers || !Array.isArray(adaptedProject.chsTeamMembers)) {
  adaptedProject.chsTeamMembers = [];
}
  
  // Ensure potentialGaps is always an array
  if (!adaptedProject.potentialGaps || !Array.isArray(adaptedProject.potentialGaps)) {
    adaptedProject.potentialGaps = [];
  }
  
  // Handle KPI/indicator fields
  if (adaptedProject.useCustomIndicator) {
    // For custom indicators, ensure the numerator and denominator are included
    if (!adaptedProject.indicatorDescription && adaptedProject.indicatorNumerator && adaptedProject.indicatorDenominator) {
      adaptedProject.indicatorDescription = `${adaptedProject.indicatorNumerator} / ${adaptedProject.indicatorDenominator}`;
    }
  } else if (adaptedProject.selectedKPI && !adaptedProject.indicatorDescription) {
    // If using predefined KPI, use that as the description if not already set
    adaptedProject.indicatorDescription = adaptedProject.selectedKPI;
  }
  
  // Ensure tool selection fields have valid values
  if (!adaptedProject.problemIdentificationTool) {
    adaptedProject.problemIdentificationTool = 'decisionMatrix';
  }
  if (!adaptedProject.rootCauseAnalysisTool) {
    adaptedProject.rootCauseAnalysisTool = 'fishbone';
  } 
  
  // Ensure project category fields have values
  if (!adaptedProject.projectCategory) {
    adaptedProject.projectCategory = 'Test of Change';
  }
  
  // Fix for root causes prioritization matrix - ensure it's properly named
  if (adaptedProject.rootCauseMatrix && Array.isArray(adaptedProject.rootCauseMatrix)) {
    adaptedProject.prioritizedRootCauses = adaptedProject.rootCauseMatrix.map((item: any) => ({
      cause: item.rootCause,
      category: item.rootCause.includes(':') ? item.rootCause.split(':')[0].trim() : 'General',
      urgency: item.urgency || 0,
      cost: item.cost || 0,
      feasibility: item.feasibility || 0,
      total: item.total || 0,
      rank: item.rank
    }));
  }
  
  // Fix for action plan - ensure it's properly named and includes all fields
  if (adaptedProject.actionPlan && Array.isArray(adaptedProject.actionPlan)) {
    adaptedProject.actionPlanItems = adaptedProject.actionPlan.map((item: any) => ({
      action: item.action || '',
      responsiblePerson: item.responsiblePerson || '',
      resources: item.resources || '',
      byWhen: item.byWhen || '',
      place: item.place || '',
      rootCause: item.rootCause || '', // Include root cause from prioritization
      status: item.status || '' // Include implementation status if present
    }));
    if (adaptedProject.actionPlanItems.length > 0) {
    }
  }
  
  // Add metadata if not present
  if (!adaptedProject.createdAt) {
    adaptedProject.createdAt = new Date().toISOString();
  }
  if (!adaptedProject.updatedAt) {
    adaptedProject.updatedAt = new Date().toISOString();
  }
  
  // FINAL VERIFICATION: Log fishbone data state before sending to backend
  if (adaptedProject.peopleCauses?.length > 0) {
  }
  
  return adaptedProject;
};

/**
 * Adapts a project from backend format to frontend format
 * Reconstructs structures like fishbone analysis
 */
export const adaptProjectFromApi = (project: any): Project => {
  // Create a deep copy
  const adaptedProject = JSON.parse(JSON.stringify(project)) as Project;
  
 
  
  // Ensure id consistency (backend uses _id, frontend may use id)
  if (adaptedProject._id && !adaptedProject.id) {
    adaptedProject.id = adaptedProject._id;
  }
  
  // Transform fishbone data from flat arrays to nested structure if needed
  transformFishboneForFrontend(adaptedProject);
  
  
  
  return adaptedProject;
};

/**
 * Adapts a test cycle from frontend format to backend format
 * Converts from the detailed TestCycle interface to the simplified backend format
 */
export const adaptTestCycleForApi = (testCycle: AddTestCycleFormData, projectId: string): any => {

  
  // Convert to backend expected format
  const adaptedTestCycle = {
    projectId,
    startDate: new Date().toISOString(), // Current date as start date
    endDate: new Date().toISOString(), // Current date as end date
    metrics: {
      numerator: testCycle.numerator || 0,
      denominator: testCycle.denominator || 0,
      percentage: testCycle.denominator ? 
        Math.round((testCycle.numerator || 0) / testCycle.denominator * 100) : 0
    },
    comments: testCycle.comment || '',
    interventionsTaken: testCycle.interventionsTaken || '',
    challengesExperienced: testCycle.challengesExperienced || '',
    lessonsLearned: testCycle.lessonsLearned || '',
    team: testCycle.team || [],
    updateTeam: testCycle.updateTeam || false,
    actionPlan: testCycle.actionPlan || [],
    sharedInForum: testCycle.sharedInForum || false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  
  return adaptedTestCycle;
};

/**
 * Adapts a test cycle from backend format to frontend format
 * Converts from the simplified backend TestCycle to the detailed frontend interface
 */
export const adaptTestCycleFromApi = (testCycle: any): TestCycle => {
  
  // Create a deep copy
  const baseTestCycle = JSON.parse(JSON.stringify(testCycle));
  
  // Ensure id consistency
  if (baseTestCycle._id && !baseTestCycle.id) {
    baseTestCycle.id = baseTestCycle._id;
  }
  
  // Convert metrics object to numerator/denominator
  const numerator = baseTestCycle.metrics?.numerator || 0;
  const denominator = baseTestCycle.metrics?.denominator || 0;
  
  // Create the adapted test cycle with the frontend expected structure
  const adaptedTestCycle: TestCycle = {
    id: baseTestCycle.id || baseTestCycle._id || '',
    projectId: baseTestCycle.projectId || '',
    numerator: numerator,
    denominator: denominator,
    testedChange: baseTestCycle.testedChange || '',
    comment: baseTestCycle.comments || '',
    interventionsTaken: baseTestCycle.interventionsTaken || '',
    challengesExperienced: baseTestCycle.challengesExperienced || '',
    lessonsLearned: baseTestCycle.lessonsLearned || '',
    team: baseTestCycle.team || [],
    updateTeam: baseTestCycle.updateTeam || false,
    actionPlan: baseTestCycle.actionPlan || [],
    sharedInForum: baseTestCycle.sharedInForum || false,
    createdAt: baseTestCycle.createdAt || new Date().toISOString(),
    metrics: baseTestCycle.metrics || {}
  };
  
  return adaptedTestCycle;
};

/**
 * Transform fishbone data from nested frontend structure to flat backend arrays
 */
function transformFishboneForBackend(project: any): void {
  if (!project.fishBoneAnalysis) return;
  
  const fishbone = project.fishBoneAnalysis;
  
  // CRITICAL FIX: Only initialize arrays if they don't already exist
  // Don't overwrite existing transformed data
  if (!project.peopleCauses) project.peopleCauses = [];
  if (!project.processCauses) project.processCauses = [];
  if (!project.policyCauses) project.policyCauses = [];
  if (!project.placeCauses) project.placeCauses = [];
  if (!project.provisionCauses) project.provisionCauses = [];
  
  // Transform each category if it exists
  if (fishbone.people && Array.isArray(fishbone.people)) {
    project.peopleCauses = fishbone.people.map((cause: any) => ({
      text: cause.text,
      why1: cause.why1 || '',
      why2: cause.why2 || '',
      why3: cause.why3 || '',
      why4: cause.why4 || '',
      why5: cause.why5 || '',
      isPrioritized: cause.isPrioritized || false
    }));
  }
  
  if (fishbone.process && Array.isArray(fishbone.process)) {
    project.processCauses = fishbone.process.map((cause: any) => ({
      text: cause.text,
      why1: cause.why1 || '',
      why2: cause.why2 || '',
      why3: cause.why3 || '',
      why4: cause.why4 || '',
      why5: cause.why5 || '',
      isPrioritized: cause.isPrioritized || false
    }));
  }
  
  if (fishbone.policy && Array.isArray(fishbone.policy)) {
    project.policyCauses = fishbone.policy.map((cause: any) => ({
      text: cause.text,
      why1: cause.why1 || '',
      why2: cause.why2 || '',
      why3: cause.why3 || '',
      why4: cause.why4 || '',
      why5: cause.why5 || '',
      isPrioritized: cause.isPrioritized || false
    }));
  }
  
  if (fishbone.place && Array.isArray(fishbone.place)) {
    project.placeCauses = fishbone.place.map((cause: any) => ({
      text: cause.text,
      why1: cause.why1 || '',
      why2: cause.why2 || '',
      why3: cause.why3 || '',
      why4: cause.why4 || '',
      why5: cause.why5 || '',
      isPrioritized: cause.isPrioritized || false
    }));
  }
  
  if (fishbone.provision && Array.isArray(fishbone.provision)) {
    project.provisionCauses = fishbone.provision.map((cause: any) => ({
      text: cause.text,
      why1: cause.why1 || '',
      why2: cause.why2 || '',
      why3: cause.why3 || '',
      why4: cause.why4 || '',
      why5: cause.why5 || '',
      isPrioritized: cause.isPrioritized || false
    }));
  }
}

/**
 * Transform fishbone data from flat backend arrays to nested frontend structure
 */
function transformFishboneForFrontend(project: any): void {
  // Check if we have fishbone data in flat structure
  const hasFlatFishboneData = 
    (project.peopleCauses && project.peopleCauses.length > 0) ||
    (project.processCauses && project.processCauses.length > 0) ||
    (project.policyCauses && project.policyCauses.length > 0) ||
    (project.placeCauses && project.placeCauses.length > 0) ||
    (project.provisionCauses && project.provisionCauses.length > 0);
  
  // If no fishbone data or already has nested structure, exit
  if (!hasFlatFishboneData || (project.fishBoneAnalysis && typeof project.fishBoneAnalysis === 'object')) {
      
    return;
  }
  
  // Create nested structure
  project.fishBoneAnalysis = {
    people: project.peopleCauses || [],
    process: project.processCauses || [],
    policy: project.policyCauses || [],
    place: project.placeCauses || [],
    provision: project.provisionCauses || []
  };
  
  // IMPORTANT: Make sure rootCauseAnalysisTool is set to 'fishbone' 
  // when we have fishbone data, as this controls rendering in the UI
  if (hasFlatFishboneData && !project.rootCauseAnalysisTool) {
    project.rootCauseAnalysisTool = 'fishbone';
  }
  
   
}
